import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { signIn, signOut, subscribeToAuthState } from '../../services/auth/authService'
import { getUserMembership } from '../../services/auth/roleService'
import { authContext, type AuthContextValue } from './context'

function authErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'We could not complete that request. Please try again.'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Pick<AuthContextValue, 'user' | 'role' | 'schoolId' | 'loading' | 'phase' | 'error'>>({
    user: null,
    role: null,
    schoolId: null,
    loading: true,
    phase: 'authenticating',
    error: null,
  })
  const authRequestRef = useRef(0)

  useEffect(() => {
    let active = true
    const unsubscribe = subscribeToAuthState(async (nextUser) => {
      if (!active) return
      const requestId = authRequestRef.current + 1
      authRequestRef.current = requestId

      if (!nextUser) {
        setState({
          user: null,
          role: null,
          schoolId: null,
          loading: false,
          phase: 'signed_out',
          error: null,
        })
        return
      }

      setState({
        user: nextUser,
        role: null,
        schoolId: null,
        loading: true,
        phase: 'membership_resolving',
        error: null,
      })
      try {
        const membership = await getUserMembership(nextUser.uid)
        if (active && authRequestRef.current === requestId) {
          setState({
            user: nextUser,
            role: membership?.role ?? null,
            schoolId: membership?.schoolId ?? null,
            loading: false,
            phase: 'resolved',
            error: null,
          })
        }
      } catch (roleError) {
        if (active && authRequestRef.current === requestId) {
          setState({
            user: nextUser,
            role: null,
            schoolId: null,
            loading: false,
            phase: 'resolved',
            error: authErrorMessage(roleError),
          })
        }
      }
    })

    return () => {
      active = false
      unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user: state.user,
    role: state.role,
    schoolId: state.schoolId,
    loading: state.loading,
    phase: state.phase,
    error: state.error,
    signInWithEmail: async (email, password) => {
      setState((current) => ({
        ...current,
        loading: true,
        phase: 'authenticating',
        error: null,
        role: null,
        schoolId: null,
      }))
      try {
        await signIn(email, password)
      } catch (signInError) {
        authRequestRef.current += 1
        setState({
          user: null,
          role: null,
          schoolId: null,
          loading: false,
          phase: 'signed_out',
          error: authErrorMessage(signInError),
        })
        throw signInError
      }
    },
    logout: async () => {
      setState((current) => ({ ...current, error: null }))
      try {
        await signOut()
      } catch (logoutError) {
        setState((current) => ({ ...current, error: authErrorMessage(logoutError) }))
        throw logoutError
      }
    },
    clearError: () => setState((current) => ({ ...current, error: null })),
  }), [state])

  return <authContext.Provider value={value}>{children}</authContext.Provider>
}
